import React, { useState } from 'react';
import CollapsibleSection from './CollapsibleSection';
import ParameterDialog from '../ParameterDialog';
import TooltipLabel from '../common/TooltipLabel';
import { FileText, Crosshair, Settings, Layers, Database, Diamond, Package, Copy, RefreshCw } from 'lucide-react';
import { uploadMediaAsset } from '../../utils/cloudApi';
import { isValidInstanceName, existingInstanceNames } from '../../utils/packageInstance';
import { isVideoMedia } from '../../utils/measureMedia';

// Upload ceiling for a media node's file. 25MB fits a one-minute 720p clip at
// about 3.4 Mbps, which is good quality — a 1:15 clip at 1.1 Mbps looked washed
// out. It exists to stop raw phone footage (30-100MB), not to police encoding
// quality — that is the author's job, as with image resolution. sam-dbam
// enforces the same number.
const MEDIA_MAX_BYTES = 25 * 1024 * 1024;

const formatMB = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)}MB`;


const TabbedNodeEditor = ({ node, onNodeUpdate, validationErrors, nodes = [], edges = [], undockedPanels = new Set(), onUndock, renderOnly }) => {
  // Advanced popup states
  const [gravityAdvOpen, setGravityAdvOpen] = useState(false);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Helper function to get collectsInput value from either location
  const getCollectsInputValue = () => {
    const rootValue = node.collectsInput;
    const dataValue = node.data?.collectsInput;

    // If either location has an explicit value, use it
    if (rootValue !== undefined) return rootValue;
    if (dataValue !== undefined) return dataValue;

    // Default to true (collects input by default)
    return true;
  };

  const handleChange = (field, value) => {
    onNodeUpdate(node.id, { [field]: value });
  };

  const handleNestedChange = (category, field, value) => {
    const currentCategory = node.data[category] || {};
    handleChange(category, { ...currentCategory, [field]: value });
  };

  // ---- FileUpload helper renderers ----

  const renderFileUploadSettings = () => (
    <div className="parameter-group">
      <h4>File Upload Settings</h4>
      <div className="form-group compact">
        <TooltipLabel htmlFor="fu-accept-list">Accept Extensions (comma-sep)</TooltipLabel>
        <input
          id="fu-accept-list"
          type="text"
          value={(node.data.accept || []).join(',')}
          onChange={(e) => handleChange('accept', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
          placeholder=".pdf,.docx,.txt"
        />
      </div>
      <div className="form-group compact">
        <TooltipLabel htmlFor="fu-max-size">Max Size (MB)</TooltipLabel>
        <input
          id="fu-max-size"
          type="number"
          min={1}
          value={node.data.maxSizeMB || 25}
          onChange={(e) => handleChange('maxSizeMB', Number(e.target.value))}
        />
      </div>
      <div className="form-group compact">
        <TooltipLabel htmlFor="fu-storage">Storage Mode</TooltipLabel>
        <select
          id="fu-storage"
          value={node.data.storage || 'auto'}
          onChange={(e) => handleChange('storage', e.target.value)}
        >
          <option value="auto">Auto</option>
          <option value="drive">Drive (dev)</option>
          <option value="gcs">GCS (prod)</option>
        </select>
      </div>
      <div className="form-group compact">
        <TooltipLabel htmlFor="fu-quarantine">Quarantine (prod only)</TooltipLabel>
        <input
          id="fu-quarantine"
          type="checkbox"
          checked={node.data.quarantine !== false}
          onChange={(e) => handleChange('quarantine', e.target.checked)}
        />
      </div>
      <div className="form-group compact">
        <TooltipLabel htmlFor="fu-required">Required</TooltipLabel>
        <input
          id="fu-required"
          type="checkbox"
          checked={!!node.data.required}
          onChange={(e) => handleChange('required', e.target.checked)}
        />
      </div>
    </div>
  );

  // ---- Section renderers ----

  const renderQuestionIdentity = () => (
    <div className="tab-content">
      <div className="tab-section">
        <div className="parameter-group">
          <h5>{node.type === 'start' ? 'Introduction' : node.type === 'end' ? 'Completion' : 'Question'} Identity</h5>

          {/* Question ID field - show for all node types */}
          <div className="form-group compact">
            <TooltipLabel htmlFor="question-id">
              {node.type === 'start' ? 'Introduction ID' : node.type === 'end' ? 'Completion ID' : 'Question ID'}
            </TooltipLabel>
            <input
              id="question-id"
              type="text"
              value={node.data.id || node.data.question_id || ''}
              onChange={(e) => handleChange(node.data.question_id !== undefined ? 'question_id' : 'id', e.target.value)}
              placeholder={node.type === 'start' ? 'START' : node.type === 'end' ? 'end_*' : 'Q1'}
              readOnly={node.type === 'start' || node.type === 'end'}
            />
          </div>

          {/* Spoken/Question Text field - show for all node types with different labels */}
          <div className="form-group compact">
            {node.type === 'start' && (
              <TooltipLabel htmlFor="question-text" keyName="introduction_text">
                Introduction Text
              </TooltipLabel>
            )}
            {node.type === 'end' && (
              <TooltipLabel htmlFor="question-text" keyName="completion_text">
                Completion Text
              </TooltipLabel>
            )}
            {!['start', 'end'].includes(node.type) && (
              <TooltipLabel htmlFor="question-text">
                {node.data.question_mode === 'intent' ? 'Data Collection Brief' : 'Question Text'}
              </TooltipLabel>
            )}
            <textarea
              id="question-text"
              value={node.data.text || ''}
              onChange={(e) => handleChange('text', e.target.value)}
              placeholder={
                node.type === 'start'
                  ? "Enter the text Sam will speak when starting the survey..."
                  : node.type === 'end'
                    ? "Enter the text Sam will speak when completing the survey..."
                    : node.data.question_mode === 'intent'
                      ? "Describe what data to collect, e.g. 'Understand daily exercise habits — frequency, types of activity, and any barriers'"
                      : "Enter your question here"
              }
              rows={3}
            />
          </div>

          {/* Hide data collection fields for Start/End nodes.
              File nodes are External Event Nodes (docs/external-event-nodes.md):
              they collect no typed input — the class decides pause behavior, so
              the Collects Input checkbox does not apply and is not shown. */}
          {!['start', 'end'].includes(node.type) && (
            <>
              {node.data.type === 'file' ? (
                <div className="form-group compact">
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    External event node: this step waits for a file upload, not a
                    typed answer. The variable below is the result slot the system
                    fills with the stored file reference.
                  </small>
                </div>
              ) : (
                <div className="form-group compact">
                  <TooltipLabel htmlFor="collects-input" keyName="collectsInput">Collects Input</TooltipLabel>
                  <input
                    id="collects-input"
                    type="checkbox"
                    checked={getCollectsInputValue()}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      // Update at root level
                      onNodeUpdate(node.id, { collectsInput: checked });
                      // If turning off input collection, clear variable immediately
                      if (!checked) {
                        onNodeUpdate(node.id, { data: { ...node.data, variable: '', variable_name: '' } });
                      }
                    }}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Uncheck for informational nodes that auto-advance.
                  </small>
                </div>
              )}

              {(node.data.type === 'file' || getCollectsInputValue()) && (
                <div className="form-group compact">
                  <TooltipLabel htmlFor="question-variable">Variable Name</TooltipLabel>
                  <input
                    id="question-variable"
                    type="text"
                    value={node.data.variable || ''}
                    onChange={(e) => handleChange('variable', e.target.value)}
                    placeholder="age, gender, zipCode, etc."
                    style={{ borderColor: /^q_q/i.test(node.data.variable || '') ? '#f0ad4e' : undefined }}
                  />
                  {/^q_q/i.test(node.data.variable || '') && (
                    <small style={{ color: '#f0ad4e', fontSize: '12px' }}>
                      Placeholder name – consider renaming for clarity
                    </small>
                  )}
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Variable name for storing this question's response (required when collecting input)
                  </small>
                </div>
              )}
              {!getCollectsInputValue() && (
                <div className="form-group compact">
                  <TooltipLabel htmlFor="question-variable-disabled">Variable Name</TooltipLabel>
                  <input id="question-variable-disabled" type="text" value="" placeholder="(not required)" disabled />
                </div>
              )}
            </>
          )}

          {/* Show system node info for Start/End nodes */}
          {['start', 'end'].includes(node.type) && (
            <div className="form-group compact">
              <div style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #e9ecef' }}>
                <small style={{ color: '#666', fontSize: '12px' }}>
                  <strong>System Node:</strong> This {node.type} node doesn't collect user input.
                  Sam will speak the text above and automatically {node.type === 'start' ? 'proceed to the first question' : 'complete the survey'}.
                </small>
              </div>
            </div>
          )}
        </div>

        {/* Question Mode — verbatim vs intent (question nodes only) */}
        {!['start', 'end'].includes(node.type) && (
          <div className="parameter-group">
            <h5>Question Mode</h5>
            <div className="form-group compact">
              <TooltipLabel htmlFor="question-mode" keyName="question_mode">Presentation Mode</TooltipLabel>
              <select
                id="question-mode"
                value={node.data.question_mode || 'verbatim'}
                onChange={(e) => handleChange('question_mode', e.target.value)}
              >
                <option value="verbatim">Verbatim – Ask the question as written</option>
                <option value="intent">Intent – AI steers conversation to collect data</option>
              </select>
              <small style={{ color: '#666', fontSize: '12px' }}>
                {(node.data.question_mode || 'verbatim') === 'verbatim'
                  ? 'Question text is spoken directly to the survey recipient.'
                  : 'Question text becomes a data collection brief — Sam will naturally guide the conversation to gather this information.'}
              </small>
            </div>
            {node.data.question_mode === 'intent' && (
              <div className="form-group compact">
                <TooltipLabel htmlFor="intent-brief" keyName="intent_brief">Intent Brief (optional)</TooltipLabel>
                <textarea
                  id="intent-brief"
                  value={node.data.intent_brief || ''}
                  onChange={(e) => handleChange('intent_brief', e.target.value)}
                  placeholder="Additional guidance for how Sam should approach collecting this data (leave empty to use question text as the brief)"
                  rows={2}
                />
              </div>
            )}
          </div>
        )}

        {/* Hide question type selection for Start/End nodes */}
        {!['start', 'end'].includes(node.type) && (
          <div className="parameter-group">
            <h5>Question Type & Options</h5>
            <div className="form-group compact">
              <TooltipLabel htmlFor="question-type" keyName="question_type">Question Type</TooltipLabel>
              <select
                id="question-type"
                value={node.data.type || 'text'}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="choice">Multiple Choice</option>
                <option value="yes_no">Yes/No</option>
                <option value="scale">Scale (1-10)</option>
                <option value="email">Email</option>
                <option value="file">File</option>
              </select>
            </div>

            {(node.data.type === 'choice') && (
              <div className="form-group compact">
                <TooltipLabel htmlFor="question-choices" keyName="options (choices)">Choices (one per line)</TooltipLabel>
                <textarea
                  id="question-choices"
                  value={(node.data.choices || []).join('\n')}
                  onKeyDown={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const rawLines = e.target.value.split('\n');
                    handleChange('choices', rawLines);
                  }}
                  placeholder={"Option 1\nOption 2\nOption 3"}
                  rows={4}
                />
              </div>
            )}
            {(node.data.type === 'text') && (
              <div className="form-group compact">
                <TooltipLabel htmlFor="question-response-categories" keyName="responseCategories">Expected Response Categories</TooltipLabel>
                <textarea
                  id="question-response-categories"
                  value={(node.data.responseCategories || []).map(b => (b && b.summary) || '').join('\n')}
                  onKeyDown={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    // Mirror the Choices box: store raw lines so Enter creates a
                    // new line. Trimming/empty-line removal happens at publish,
                    // not on keystroke (trimming here deletes the newline).
                    const existing = node.data.responseCategories || [];
                    const next = e.target.value.split('\n').map((line, i) => ({ ...(existing[i] || {}), summary: line }));
                    handleChange('responseCategories', next);
                  }}
                  placeholder={"negative\ngenerally negative\nneutral\ngenerally positive\npositive"}
                  rows={4}
                />
              </div>
            )}

            {(node.data.type === 'number' || node.data.type === 'scale') && (
              <div className="form-row">
                <div className="form-group">
                  <TooltipLabel htmlFor="min-value" keyName="min_value">Minimum Value</TooltipLabel>
                  <input
                    id="min-value"
                    type="number"
                    value={node.data.minValue || ''}
                    onChange={(e) => handleChange('minValue', e.target.value ? parseInt(e.target.value) : null)}
                  />
                </div>
                <div className="form-group">
                  <TooltipLabel htmlFor="max-value" keyName="max_value">Maximum Value</TooltipLabel>
                  <input
                    id="max-value"
                    type="number"
                    value={node.data.maxValue || ''}
                    onChange={(e) => handleChange('maxValue', e.target.value ? parseInt(e.target.value) : null)}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="parameter-group">
          <h5>Contextual Information</h5>
          <div className="form-group compact">
            <TooltipLabel htmlFor="question-context" keyName="context">Context (for Sam)</TooltipLabel>
            <textarea
              id="question-context"
              value={node.data.context || ''}
              onChange={(e) => handleChange('context', e.target.value)}
              placeholder="Background context to help Sam understand this question"
              rows={2}
            />
          </div>

          <div className="form-group compact">
            <TooltipLabel htmlFor="help-text" keyName="help_text">Help Text</TooltipLabel>
            <textarea
              id="help-text"
              value={node.data.helpText || ''}
              onChange={(e) => handleChange('helpText', e.target.value)}
              placeholder="Additional guidance for respondents"
              rows={2}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <TooltipLabel htmlFor="question-category" keyName="category">Category</TooltipLabel>
              <input
                id="question-category"
                type="text"
                value={node.data.category || ''}
                onChange={(e) => handleChange('category', e.target.value)}
                placeholder="Demographics, Health, etc."
              />
            </div>
            <div className="form-group">
              <TooltipLabel keyName="required" className="checkbox-label">
                <input
                  type="checkbox"
                  checked={node.data.required || false}
                  onChange={(e) => handleChange('required', e.target.checked)}
                />
                Required question
              </TooltipLabel>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPersonalityControls = () => (
    <div className="tab-content">
      <div className="tab-section">
        <div className="parameter-group">
          <h5><Crosshair size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Gravity Configuration</h5>
          <div className="form-group compact">
            <TooltipLabel htmlFor="clarification-policy" keyName="clarificationPolicy">Clarification Policy</TooltipLabel>
            <select
              id="clarification-policy"
              value={node.data.clarificationPolicy || 'auto'}
              onChange={(e) => handleChange('clarificationPolicy', e.target.value)}
            >
              <option value="auto">Auto – let Sam decide</option>
              <option value="never">Never – always advance</option>
            </select>
          </div>

          <div className="form-group compact">
            <TooltipLabel htmlFor="gravity-strength" keyName="gravity.strength">Gravity Strength</TooltipLabel>
            <select
              id="gravity-strength"
              value={node.data.gravity?.strength || 'medium'}
              onChange={(e) => handleNestedChange('gravity', 'strength', e.target.value)}
            >
              <option value="low">Low - Gentle guidance</option>
              <option value="medium">Medium - Balanced control</option>
              <option value="high">High - Firm redirection</option>
              <option value="critical">Critical - Maximum pressure</option>
            </select>
          </div>

          <div className="form-group compact">
            <TooltipLabel htmlFor="drift-tolerance" keyName="gravity.driftTolerance">Drift Tolerance</TooltipLabel>
            <select
              id="drift-tolerance"
              value={node.data.gravity?.driftTolerance || 'moderate'}
              onChange={(e) => handleNestedChange('gravity', 'driftTolerance', e.target.value)}
            >
              <option value="strict">Strict - Immediate redirection</option>
              <option value="moderate">Moderate - Balanced approach</option>
              <option value="flexible">Flexible - Allow conversation</option>
            </select>
          </div>

          <div className="form-group compact">
            <TooltipLabel htmlFor="redirection-style" keyName="gravity.redirectionStyle">Redirection Style</TooltipLabel>
            <select
              id="redirection-style"
              value={node.data.gravity?.redirectionStyle || 'gentle'}
              onChange={(e) => handleNestedChange('gravity', 'redirectionStyle', e.target.value)}
            >
              <option value="gentle">Gentle - Soft guidance</option>
              <option value="direct">Direct - Clear direction</option>
              <option value="forceful">Forceful - Strong pressure</option>
            </select>
          </div>
          {/* Advanced Gravity Controls button */}
          <button type="button" className="btn-small" style={{ marginTop: '12px' }}
            onClick={() => setGravityAdvOpen(true)} title="Open advanced gravity configuration"><Settings size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Advanced Gravity Controls…</button>
        </div>

        <div className="parameter-group">
          <h5><Layers size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Breadcrumb System</h5>
          <div className="parameter-group-description">
            Context management for AI conversation intelligence
          </div>

          {/* Context Depth, Priority Data and Include conversation history were
              removed (2026-08-02). The kernel sends the complete question trail
              and the full verbatim exchange for the current question on every
              turn, so depth limits and hand-picked priority fields have nothing
              left to control. Relevance Criteria stays as the designated lever
              for the very-long-survey case — see the note below. */}
          <div className="form-group compact">
            <TooltipLabel htmlFor="relevance-criteria" keyName="breadcrumbs.relevanceCriteria">Relevance Criteria</TooltipLabel>
            <select
              id="relevance-criteria"
              value={node.data.breadcrumbs?.relevanceCriteria || 'medium'}
              onChange={(e) => handleNestedChange('breadcrumbs', 'relevanceCriteria', e.target.value)}
            >
              <option value="low">Low - Include broad context</option>
              <option value="medium">Medium - Balanced relevance</option>
              <option value="high">High - Strict relevance only</option>
            </select>
            <small style={{ color: '#666', fontSize: '12px' }}>
              Relevance Criteria is a control for very long surveys, where the
              full trail costs more than it is worth.
            </small>
          </div>
        </div>
      </div>
    </div>
  );

  /**
   * The Data section is file-upload configuration and nothing else.
   * Capture Scope and Advanced Data Capture were removed (2026-08-02): they
   * belonged to a selective-capture model the kernel no longer runs — every
   * turn, voice and data alike, is captured — and no kernel code ever read
   * them. The section is now rendered only for file nodes.
   */
  const renderDataCapture = () => renderFileUploadSettings();

  // --- Decision node editor (minimal — branches live on edges) ---
  if (node.type === 'package') {
    const d = node.data || {};
    const instanceName = d.instanceName || '';
    const variables = Array.isArray(d.variables) ? d.variables : [];
    // Collision check: instance names must be unique among the host's packages.
    const otherNames = existingInstanceNames(nodes.filter(n => n.id !== node.id));
    const nameValid = isValidInstanceName(instanceName);
    const nameCollides = otherNames.has(instanceName);
    const copy = (text) => { try { navigator.clipboard?.writeText(text); } catch (_) {} };

    return (
      <div className="node-editor-accordion">
        <CollapsibleSection title={<><Package size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Package</>} subtitle="Reference to a library package" defaultCollapsed={false}>
          <div className="tab-content">
            <div className="tab-section">
              <div className="parameter-group">
                <h5>{d.title || 'Untitled package'}</h5>
                {d.description && <div style={{ fontSize: 12, color: '#556', marginBottom: 6 }}>{d.description}</div>}
                <div style={{ fontSize: 12, color: '#778', display: 'flex', gap: 14 }}>
                  <span>Version {d.packageVersion ?? d.version ?? '?'}</span>
                  <span>{d.questionCount ?? variables.length} question(s)</span>
                </div>
              </div>

              <div className="parameter-group">
                <h5>Instance name</h5>
                <div className="form-group compact">
                  <input
                    type="text"
                    value={instanceName}
                    onChange={(e) => handleChange('instanceName', e.target.value)}
                    placeholder="explain_2"
                    style={(!nameValid || nameCollides) ? { borderColor: '#d9534f' } : undefined}
                  />
                </div>
                {!nameValid && <div style={{ color: '#d9534f', fontSize: 11 }}>Must start with a letter; letters, digits, underscore only.</div>}
                {nameValid && nameCollides && <div style={{ color: '#d9534f', fontSize: 11 }}>Another package instance already uses this name.</div>}
                <div style={{ fontSize: 11, color: '#889', marginTop: 2 }}>You rename the instance, not the package. Prefixing happens at compile — renaming is free until save.</div>
              </div>

              {variables.length > 0 && (
                <div className="parameter-group">
                  <h5>Effective variables</h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {variables.map((v) => {
                      const eff = `${instanceName || 'instance'}__${v}`;
                      return (
                        <div key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                          <code style={{ background: '#F2F5F8', padding: '2px 6px', borderRadius: 3, flex: 1 }}>{eff}</code>
                          <button className="btn-small" title="Copy" onClick={() => copy(eff)} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Copy size={11} /></button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="parameter-group">
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    className="btn-small"
                    onClick={() => { try { window.dispatchEvent(new CustomEvent('package-rebind-request', { detail: { nodeId: node.id } })); } catch (_) {} }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <Package size={12} /> Change package…
                  </button>
                  <button
                    className="btn-small"
                    onClick={() => { try { window.dispatchEvent(new CustomEvent('package-refresh-request', { detail: { nodeId: node.id } })); } catch (_) {} }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <RefreshCw size={12} /> Refresh to latest
                  </button>
                </div>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </div>
    );
  }

  if (node.type === 'decision') {
    // Find predecessor input node for variable inheritance display
    const incomingEdge = edges.find(e => e.target === node.id);
    const sourceNode = incomingEdge ? nodes.find(n => n.id === incomingEdge.source) : null;
    const inheritedVar = sourceNode?.data?.variable || sourceNode?.data?.variable_name || '';
    const questionType = sourceNode?.data?.type || 'text';

    // Count outgoing edges for display
    const outgoingEdges = edges.filter(e => e.source === node.id);

    return (
      <div className="node-editor-accordion">
        <CollapsibleSection title={<><Diamond size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Decision Node</>} subtitle="AI-driven branch routing" defaultCollapsed={false}>
          <div className="tab-content">
            <div className="tab-section">
              <div className="parameter-group">
                <h5>Decision ID</h5>
                <div className="form-group compact">
                  <input
                    type="text"
                    value={node.data.id || ''}
                    onChange={(e) => handleChange('id', e.target.value)}
                    placeholder="D1"
                  />
                </div>
              </div>

              <div className="parameter-group">
                <h5>Label</h5>
                <div className="form-group compact">
                  <input
                    type="text"
                    value={node.data.label || node.data.text || ''}
                    onChange={(e) => handleChange('label', e.target.value)}
                    placeholder="e.g. Route by color preference"
                  />
                </div>
              </div>

              {/* Inherited variable info */}
              {inheritedVar ? (
                <div className="parameter-group">
                  <div style={{ padding: '8px', backgroundColor: '#eff6ff', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
                    <small style={{ color: '#1d4ed8', fontSize: '12px' }}>
                      <strong>Inherited variable:</strong> <code>{inheritedVar}</code>
                      {sourceNode?.data?.id && <> from <strong>{sourceNode.data.id}</strong></>}
                      {questionType !== 'text' && <> (type: <em>{questionType}</em>)</>}
                    </small>
                  </div>
                </div>
              ) : (
                <div className="parameter-group">
                  <div style={{ padding: '8px', backgroundColor: '#fef3c7', borderRadius: '4px', border: '1px solid #fcd34d' }}>
                    <small style={{ color: '#92400e', fontSize: '12px' }}>
                      Connect an input node upstream to inherit its variable.
                    </small>
                  </div>
                </div>
              )}

              {/* Outgoing edges summary */}
              <div className="parameter-group">
                <div style={{ padding: '8px', backgroundColor: '#f9fafb', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    <strong>{outgoingEdges.length}</strong> outgoing edge{outgoingEdges.length !== 1 ? 's' : ''}.
                    Select an edge to configure its condition and commentary.
                  </small>
                </div>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </div>
    );
  }

  // --- Pleasant node editor ---
  // NOTE: the content guard (no off-color / group-targeted / injury / illness /
  // money-trouble material, never at the respondent's expense) is fixed text in
  // the kernel prompt builder. It is deliberately NOT exposed here — there is no
  // checkbox and no advanced field, so it cannot be switched off by accident.
  // See docs/system documentation library/nodal_types.md.
  if (node.type === 'pleasant') {
    return (
      <div className="node-editor-accordion">
        <CollapsibleSection title="Pleasant Node" subtitle="Sam says a short joke or pleasantry, then moves on" defaultCollapsed={false}>
          <div className="tab-content">
            <div className="tab-section">
              <div className="parameter-group">
                <h5>Identity</h5>
                <div className="form-group compact">
                  <TooltipLabel htmlFor="pleasant-node-id">Node ID</TooltipLabel>
                  <input
                    id="pleasant-node-id"
                    type="text"
                    value={node.data.id || ''}
                    onChange={(e) => handleChange('id', e.target.value)}
                    placeholder="P1"
                  />
                </div>
              </div>

              <div className="parameter-group">
                <h5>Options</h5>
                <div className="form-group compact">
                  <TooltipLabel htmlFor="pleasant-length">Length</TooltipLabel>
                  <select
                    id="pleasant-length"
                    value={node.data.length || 'one_line'}
                    onChange={(e) => handleChange('length', e.target.value)}
                  >
                    <option value="one_line">One line</option>
                    <option value="two_lines">Two lines</option>
                  </select>
                </div>

                <div className="form-group compact">
                  <TooltipLabel htmlFor="pleasant-scope">Scope</TooltipLabel>
                  <select
                    id="pleasant-scope"
                    value={node.data.scope || 'in_context'}
                    onChange={(e) => handleChange('scope', e.target.value)}
                  >
                    <option value="in_context">In context (uses the survey topic)</option>
                    <option value="standalone">Standalone (generic)</option>
                  </select>
                </div>

                <div className="form-group compact">
                  <TooltipLabel htmlFor="pleasant-flavor">Flavor</TooltipLabel>
                  <select
                    id="pleasant-flavor"
                    value={node.data.flavor || 'joke'}
                    onChange={(e) => handleChange('flavor', e.target.value)}
                  >
                    <option value="joke">Joke</option>
                    <option value="pleasantry">Pleasantry</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </div>
    );
  }

  // --- Media node editor ---
  if (node.type === 'media') {
    return (
      <div className="node-editor-accordion">
        <CollapsibleSection title="Media Node" subtitle="Display or dismiss a media stimulus" defaultCollapsed={false}>
          <div className="tab-content">
            <div className="tab-section">
              <div className="parameter-group">
                <h5>Identity</h5>
                <div className="form-group compact">
                  <TooltipLabel htmlFor="media-node-id" keyName="id">Node ID</TooltipLabel>
                  <input
                    id="media-node-id"
                    type="text"
                    value={node.data.id || ''}
                    onChange={(e) => handleChange('id', e.target.value)}
                    placeholder="M1"
                  />
                </div>
                <div className="form-group compact">
                  <TooltipLabel htmlFor="media-stimulus-id" keyName="stimulus_id">Pair ID</TooltipLabel>
                  <input
                    id="media-stimulus-id"
                    type="text"
                    value={node.data.stimulus_id || ''}
                    onChange={(e) => handleChange('stimulus_id', e.target.value)}
                    placeholder="hero_image"
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Links this node with its paired show/hide counterpart — both must use the same Pair ID.
                  </small>
                </div>
              </div>

              <div className="parameter-group">
                <h5>Action</h5>
                <div className="form-group compact">
                  <TooltipLabel htmlFor="media-action">Show / Hide</TooltipLabel>
                  <select
                    id="media-action"
                    value={node.data.show === false ? 'false' : 'true'}
                    onChange={(e) => handleChange('show', e.target.value === 'true')}
                  >
                    <option value="true">Show (display stimulus)</option>
                    <option value="false">Hide (dismiss stimulus)</option>
                  </select>
                </div>
              </div>

              {node.data.show !== false && (
                <div className="parameter-group">
                  <h5>Media Source</h5>
                  <div className="form-group compact">
                    <TooltipLabel htmlFor="media-file-picker" keyName="media.src">Load Image File</TooltipLabel>
                    <input
                      // Key the input by node id so React remounts a fresh file
                      // field whenever a different media node is selected. Without
                      // this, React reuses the same uncontrolled <input> DOM
                      // element across selections and the browser keeps showing
                      // the previously chosen filename — making every media node
                      // appear to share one file. The per-node key forces a clean,
                      // empty field for each node.
                      key={`media-file-picker-${node.id}`}
                      id="media-file-picker"
                      type="file"
                      accept="image/*,video/mp4"
                      style={{ marginBottom: 4 }}
                      onChange={async (e) => {
                        // Capture the input element before awaiting so we can
                        // clear it afterward — this lets re-picking the same file
                        // on the same node fire onChange again and leaves no stale
                        // filename in the field.
                        const inputEl = e.currentTarget;
                        const file = e.target.files?.[0];
                        if (!file) return;
                        // Checked here so the author is told immediately rather
                        // than after waiting through an upload that then fails.
                        // The server enforces the same limit; this is only the
                        // fast path, not the gate.
                        if (file.size > MEDIA_MAX_BYTES) {
                          setUploadError(
                            `${file.name} is ${formatMB(file.size)}. The limit is ${formatMB(MEDIA_MAX_BYTES)}. ` +
                            `Re-export it smaller and try again.`
                          );
                          if (inputEl) inputEl.value = '';
                          return;
                        }
                        setMediaUploading(true);
                        setUploadError(null);
                        try {
                          const res = await uploadMediaAsset(file);
                          // type drives which element the delivery page builds,
                          // <img> or <video>. Nothing else sets it, and getting
                          // it wrong renders the wrong tag.
                          const isVideo = String(file.type || '').startsWith('video/');
                          handleChange('media', {
                            ...(node.data.media || {}),
                            src: res.url,
                            asset_id: res.asset_id,
                            originalName: file.name,
                            type: isVideo ? 'video' : 'image',
                            mime_type: file.type || ''
                          });
                        } catch (err) {
                          setUploadError(err.message || 'Upload failed');
                        } finally {
                          setMediaUploading(false);
                          if (inputEl) inputEl.value = '';
                        }
                      }}
                    />
                    {mediaUploading && <div style={{ fontSize: 12, color: '#2563eb', marginTop: 4 }}>Uploading media file...</div>}
                    {uploadError && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{uploadError}</div>}
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Select a local image or MP4 video. It will be uploaded to SamSurveyor asset
                      storage. Supply images at 150&nbsp;dpi or better — respondents see them scaled
                      up to fill their screen, not at this thumbnail size. Video must be silent and
                      short; it plays automatically and loops until the respondent answers.
                      Maximum {formatMB(MEDIA_MAX_BYTES)}.
                    </small>
                  </div>
                  {node.data.media?.originalName && (
                    <div style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>
                      Loaded: {node.data.media.originalName}
                    </div>
                  )}
                  <div className="form-group compact">
                    <TooltipLabel htmlFor="media-alt" keyName="media.alt">Alt Text</TooltipLabel>
                    <input
                      id="media-alt"
                      type="text"
                      value={node.data.alt || ''}
                      onChange={(e) => handleChange('alt', e.target.value)}
                      placeholder="A photo of the product"
                    />
                  </div>
                  {/* No display-size field. The presentor sizes the image from
                      the space the respondent's screen actually has, and picks
                      between the side panel and the pinned top band using the
                      natural dimensions stamped in at compile time. A hand-set
                      pixel width could only ever be right on one screen. */}
                  {/* Thumbnail preview — images only. Video is deliberately not
                      previewed here: authors prepare clips in real video tools,
                      and the file is assumed launch-ready. The <img> tag has to
                      be skipped rather than left to fail, or an MP4 renders as a
                      broken-image icon. */}
                  {node.data.media?.src && !isVideoMedia(node.data.media) && (
                    <div style={{ marginTop: 8 }}>
                      <img
                        src={node.data.media.src}
                        alt={node.data.alt || 'preview'}
                        style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 4, border: '1px solid #e5e7eb' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="parameter-group">
                <h5>Tools</h5>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="btn-small" title="Create a paired hide node to dismiss this media" onClick={() => {
                    const evt = new CustomEvent('graph-create-media-hide', { detail: { showNodeId: node.id, stimulus_id: node.data?.stimulus_id || '' } });
                    window.dispatchEvent(evt);
                  }}>Create paired hide node</button>
                  {node.data.show !== false && node.data.media?.src && (
                    <button type="button" className="btn-small" title="Test if the media URL is reachable" onClick={async () => {
                      const url = (node.data?.media?.src || '').trim();
                      if (!url) return;
                      try {
                        const ctrl = new AbortController();
                        const t = setTimeout(() => ctrl.abort(), 2000);
                        let ok = false;
                        try {
                          const head = await fetch(url, { method: 'HEAD', signal: ctrl.signal, cache: 'no-store' });
                          ok = head.ok;
                        } catch (_) { }
                        if (!ok) {
                          const getResp = await fetch(url, { method: 'GET', signal: ctrl.signal, cache: 'no-store' });
                          ok = getResp.ok;
                        }
                        clearTimeout(t);
                        alert(ok ? '✓ Media reachable' : '✗ Media not reachable (HTTP error)');
                      } catch (e) {
                        alert('✗ Media not reachable (network/timeout)');
                      }
                    }}>Check reachability</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </div>
    );
  }

  // Only show full configuration for question, start, and end nodes
  if (!['question', 'start', 'end'].includes(node.type)) {
    // For other node types (like package), use simplified editor
    return (
      <CollapsibleSection title={`${node.type} Editor`} defaultCollapsed={false}>
        <div className="no-selection">
          <p>{node.type} nodes don't have configurable properties.</p>
        </div>
      </CollapsibleSection>
    );
  }

  // --- Extract ParameterDialogs so they can be used from both renderOnly and normal paths ---
  const gravityAdvDialog = (
    <ParameterDialog
      open={gravityAdvOpen}
      onClose={() => setGravityAdvOpen(false)}
      title="Advanced Gravity Controls"
      description="Fine-tune Sam's conversational behaviour for this question."
      fields={[
        { key: 'tone', label: 'Tone', type: 'select', options: [{ value: 'friendly', label: 'Friendly' }, { value: 'formal', label: 'Formal' }, { value: 'humorous', label: 'Humorous' }, { value: 'professional', label: 'Professional' }] },
        { key: 'verbosity', label: 'Verbosity', type: 'select', options: [{ value: 'brief', label: 'Brief' }, { value: 'moderate', label: 'Moderate' }, { value: 'verbose', label: 'Verbose' }] },
        { key: 'acknowledgment_style', label: 'Acknowledgment Style', type: 'select', options: [{ value: 'warm', label: 'Warm' }, { value: 'neutral', label: 'Neutral' }] },
        { key: 'acknowledge_input', label: 'Acknowledge Input', type: 'select', options: [{ value: 'always', label: 'Always' }, { value: 'never', label: 'Never' }] },
        { key: 'provide_encouragement', label: 'Provide Encouragement', type: 'checkbox' },
        { key: 'explain_next_steps', label: 'Explain Next Steps', type: 'checkbox' },
        { key: 'allow_tangents', label: 'Allow Tangents', type: 'checkbox' }
      ]}
      values={{
        tone: node.data?.conversational?.sam_persona?.tone || 'friendly',
        verbosity: node.data?.conversational?.sam_persona?.verbosity || 'moderate',
        acknowledgment_style: node.data?.conversational?.sam_persona?.acknowledgment_style || 'warm',
        acknowledge_input: node.data?.conversational?.response_requirements?.acknowledge_input ? 'always' : 'never',
        provide_encouragement: node.data?.conversational?.response_requirements?.provide_encouragement ?? true,
        explain_next_steps: node.data?.conversational?.response_requirements?.explain_next_steps ?? false,
        allow_tangents: node.data?.conversational?.conversation_management?.allow_tangents ?? true
      }}
      onSave={(vals) => {
        const conversational = {
          ...(node.data.conversational || {}),
          sam_persona: {
            ...(node.data.conversational?.sam_persona || {}),
            tone: vals.tone,
            verbosity: vals.verbosity,
            acknowledgment_style: vals.acknowledgment_style
          },
          response_requirements: {
            ...(node.data.conversational?.response_requirements || {}),
            acknowledge_input: vals.acknowledge_input === 'always',
            provide_encouragement: vals.provide_encouragement,
            explain_next_steps: vals.explain_next_steps
          },
          conversation_management: {
            ...(node.data.conversational?.conversation_management || {}),
            allow_tangents: vals.allow_tangents
          }
        };
        onNodeUpdate(node.id, { conversational });
      }}
    />
  );

  // --- renderOnly mode: render a single section's content for floating panels ---
  if (renderOnly) {
    const sectionMap = {
      'node-identity': renderQuestionIdentity,
      'node-personality': renderPersonalityControls,
      'node-data-capture': renderDataCapture,
    };
    const renderer = sectionMap[renderOnly];
    if (!renderer) return null;
    // Include ParameterDialogs so "Advanced" buttons still work in floating panels
    return (
      <>
        {renderer()}
        {renderOnly === 'node-personality' && gravityAdvDialog}
      </>
    );
  }

  return (
    <>
      <div className="node-editor-accordion">
        {!undockedPanels.has('node-identity') && (<CollapsibleSection
          title={<><FileText size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Question</>}
          subtitle="Core question text, type, and variable"
          defaultCollapsed={false}
          sectionId="node-identity"
          onUndock={onUndock}
        >
          {renderQuestionIdentity()}
        </CollapsibleSection>)}

        {!undockedPanels.has('node-personality') && (<CollapsibleSection
          title={<><Crosshair size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Personality</>}
          subtitle="Gravity, clarification, and conversation style"
          defaultCollapsed={true}
          sectionId="node-personality"
          onUndock={onUndock}
        >
          {renderPersonalityControls()}
        </CollapsibleSection>)}

        {/* Data carries file-upload settings only, so it appears on file nodes
            alone. The Validation section was removed entirely (2026-08-02) —
            regex patterns and length rules are form-era thinking; the kernel
            never enforced them, and authored intent belongs in Context or
            Help Text where the model actually reads it. */}
        {node.data?.type === 'file' && !undockedPanels.has('node-data-capture') && (
          <CollapsibleSection
            title={<><Database size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Data</>}
            subtitle="File upload settings"
            defaultCollapsed={true}
            sectionId="node-data-capture"
            onUndock={onUndock}
          >
            {renderDataCapture()}
          </CollapsibleSection>
        )}

        {/* Media authoring helpers (beta) */}
        {node.data?.type === 'media' && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #eee' }}>
            <h4>Media (beta)</h4>
            <div className="form-row">
              <div className="form-group">
                <TooltipLabel htmlFor="media-stimulus" keyName="stimulus_id">Stimulus ID</TooltipLabel>
                <input
                  id="media-stimulus"
                  type="text"
                  value={node.data.stimulus_id || ''}
                  onChange={(e) => handleChange('stimulus_id', e.target.value)}
                />
              </div>
              <div className="form-group">
                <TooltipLabel htmlFor="media-show">Show?</TooltipLabel>
                <select
                  id="media-show"
                  value={node.data.show === false ? 'false' : 'true'}
                  onChange={(e) => handleChange('show', e.target.value === 'true')}
                >
                  <option value="true">Show (display)</option>
                  <option value="false">Hide (dismiss)</option>
                </select>
              </div>
            </div>
            {node.data.show !== false && (
              <div className="form-group">
                <TooltipLabel htmlFor="media-src" keyName="media.src">Media URL (HTTPS)</TooltipLabel>
                <input
                  id="media-src"
                  type="url"
                  value={node.data.media?.src || ''}
                  onChange={(e) => handleChange('media', { ...(node.data.media || {}), src: e.target.value })}
                  placeholder="https://.../image.png"
                />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn-small" title="Create a paired hide node to dismiss this media" onClick={() => {
                const evt = new CustomEvent('graph-create-media-hide', { detail: { showNodeId: node.id, stimulus_id: node.data?.stimulus_id || '' } });
                window.dispatchEvent(evt);
              }}>Create paired hide</button>
              <button type="button" className="btn-small" title="Test if the media URL is reachable" onClick={async () => {
                const url = (node.data?.media?.src || '').trim();
                if (!url) return;
                try {
                  const ctrl = new AbortController();
                  const t = setTimeout(() => ctrl.abort(), 2000);
                  let ok = false;
                  try {
                    const head = await fetch(url, { method: 'HEAD', signal: ctrl.signal, cache: 'no-store' });
                    ok = head.ok;
                  } catch (_) { }
                  if (!ok) {
                    const getResp = await fetch(url, { method: 'GET', signal: ctrl.signal, cache: 'no-store' });
                    ok = getResp.ok;
                  }
                  clearTimeout(t);
                  alert(ok ? 'Media reachable' : 'Media not reachable (HTTP error)');
                } catch (e) {
                  alert('Media not reachable (network/timeout)');
                }
              }}>Check reachability</button>
            </div>
          </div>
        )}
      </div>

      {gravityAdvDialog}
    </>
  );
};

export default TabbedNodeEditor; 